import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Grid } from '../components/ui/Grid';
import { Swiper } from '../components/ui/Swiper';
import { LoadMore } from '../components/ui/LoadMore';
import { Headline } from '../components/ui/Headline';
import { Plus } from 'lucide-react';
import { BandPanel } from '../components/BandPanel';
import { Filters } from '../components/Filters';
import { SEOHead } from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useLocation } from '../hooks/useLocation';
import { fetchBands, fetchFeaturedBands, type BandWithDistance } from '../services/api/bands';
import { GENRES, BAND_STATUSES } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';

const ITEMS_PER_PAGE = 12;

function Bands() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { getTranslatedGenres, getTranslatedBandStatuses } = useMusicTranslations();
  const [bands, setBands] = useState<BandWithDistance[]>([]);
  const [featuredBands, setFeaturedBands] = useState<BandWithDistance[]>([]);
  const [userBandsCount, setUserBandsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [nearMeEnabled, setNearMeEnabled] = useState(false);
  const { coordinates, loading: locationLoading } = useLocation();

  // Load featured bands once on mount
  useEffect(() => {
    const loadFeaturedBands = async () => {
      try {
        const featured = await fetchFeaturedBands(5, coordinates);
        setFeaturedBands(featured);
      } catch (error) {
        console.error('Error loading featured bands:', error);
      }
    };

    loadFeaturedBands();
  }, [coordinates]);

  const loadBands = async (start = 0, replace = true) => {
    try {
      setLoadingMore(!replace);
      
      const { bands: newBands, totalCount } = await fetchBands(
        start / ITEMS_PER_PAGE,
        ITEMS_PER_PAGE,
        searchQuery,
        selectedGenres,
        selectedLocations,
        selectedStatuses,
        coordinates,
        nearMeEnabled
      );

      setBands(prev => replace ? newBands : [...prev, ...newBands]);
      setHasMore(totalCount ? start + ITEMS_PER_PAGE < totalCount : false);
    } catch (error) {
      console.error('Error loading bands:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(0);
    loadBands(0);
  }, [searchQuery, selectedGenres, selectedLocations, selectedStatuses, nearMeEnabled]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadBands(nextPage * ITEMS_PER_PAGE, false);
  };

  // Get unique locations from bands
  const locations = Array.from(new Set(bands.map(b => b.location).filter(Boolean)));

  const translatedGenres = getTranslatedGenres();
  const translatedBandStatuses = getTranslatedBandStatuses();

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleLocationToggle = (location: string) => {
    setSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedGenres([]);
    setSelectedLocations([]);
    setSelectedStatuses([]);
    setNearMeEnabled(false);
  };

  const canCreateBand = profile?.is_musician && !userBandsCount;

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title subtitle={t('bands.subtitle')}>
          {t('bands.title')}
        </Title>
        <div className="text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Bands in Deiner Nähe"
        description="Entdecke Bands in der Schweiz. Finde neue Musik, vernetze dich mit Musikern und erfahre mehr über die lokale Musikszene."
        keywords={['Bands Schweiz', 'Schweizer Bands', 'Musik Schweiz', 'lokale Bands', 'Musikszene Schweiz', 'Band finden']}
        url="https://jamnation.ch/bands"
      />

      <div className="container mx-auto px-6 py-8 md:py-24">
        <div className="flex justify-between items-start">
          <Title 
            subtitle={t('bands.subtitle')}
          >
            {t('bands.title')}
          </Title>
        </div>

        {featuredBands.length > 0 && (
          <div className="mb-24">
            <Headline title={t('home.sections.featuredBands')} />
            <Swiper>
              {featuredBands.map((band) => (
                <BandPanel key={band.id} band={band} />
              ))}
            </Swiper>
          </div>
        )}

        <div>
          <div className="max-w-4xl mx-auto mb-8">
            <div className="text-xs text-gray-400 mb-2">FILTER</div>
            <Filters
              searchPlaceholder={t('common.search')}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              filterGroups={[
                {
                  name: t('musicians.filters.genres'),
                  options: translatedGenres.map(genre => ({
                    value: genre.value,
                    label: genre.label,
                    count: bands.filter(b => b.genres?.includes(genre.value)).length,
                  })),
                  selected: selectedGenres,
                  onToggle: handleGenreToggle,
                },
                {
                  name: t('musicians.filters.location'),
                  options: locations.map(location => ({
                    value: location || '',
                    label: location || '',
                    count: bands.filter(b => b.location === location).length,
                  })),
                  selected: selectedLocations,
                  onToggle: handleLocationToggle,
                },
                {
                  name: 'Status',
                  options: translatedBandStatuses.map(status => ({
                    value: status.value,
                    label: status.label,
                    count: bands.filter(b => b.status === status.value).length,
                  })),
                  selected: selectedStatuses,
                  onToggle: handleStatusToggle,
                },
              ]}
              onClearAll={handleClearFilters}
              nearMeFilter={{
                enabled: nearMeEnabled,
                onToggle: () => setNearMeEnabled(!nearMeEnabled),
                available: !locationLoading && !!coordinates,
              }}
            />
          </div>

          <Headline title={t('bands.title')} />
          <Grid cols={4}>
            {bands.map((band) => (
              <BandPanel key={band.id} band={band} nearMeEnabled={nearMeEnabled} />
            ))}
          </Grid>

          {bands.length === 0 && (
            <div className="text-center text-light/60 py-12">
              No bands found.
            </div>
          )}

          <LoadMore
            onClick={handleLoadMore}
            loading={loadingMore}
            hasMore={hasMore}
          />
        </div>
      </div>
    </>
  );
}

export default Bands;